import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, inr, fmtDate, loadRazorpay } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Chip } from "../components/ui";
import {
  Crown, Star, Shield, Zap, BarChart, Code, Lock, Users,
  Download, Globe, Brain, Palette, ArrowRight, Check,
  X, CreditCard, Smartphone, Mail, AlertCircle, Info,
  Calendar, RotateCcw, FileText, ArrowUpRight, DollarSign
} from "lucide-react";

const PLANS = {
  starter: {
    id: "starter",
    name: "Cricso Starter",
    badge: "Free",
    badgeColor: "success",
    description: "Perfect for small cricket groups",
    maxClubs: 2,
    priceMonthly: 0,
    priceYearly: 0,
    yearlySavings: 0,
    features: [
      { icon: Users, label: "Up to 2 Clubs" },
      { icon: Users, label: "Unlimited Members" },
      { icon: Shield, label: "Match Polls" },
      { icon: Zap, label: "Team Generation" },
      { icon: Shield, label: "Expense Tracking" },
      { icon: Shield, label: "Match Fee Split" },
      { icon: BarChart, label: "Basic Ledger" },
      { icon: Globe, label: "Join Links" },
      { icon: Mail, label: "Community Support" },
    ],
  },
  league: {
    id: "league",
    name: "Cricso League",
    badge: "Popular",
    badgeColor: "default",
    description: "Ideal for regular cricket organizers",
    maxClubs: 5,
    priceMonthly: 300,
    priceYearly: 2400,
    yearlySavings: 1200,
    features: [
      { icon: Users, label: "Up to 5 Clubs" },
      { icon: Users, label: "Unlimited Members" },
      { icon: Shield, label: "Match Polls" },
      { icon: Zap, label: "Team Generation" },
      { icon: Shield, label: "Expense Tracking" },
      { icon: Shield, label: "Match Fee Split" },
      { icon: BarChart, label: "Basic Ledger" },
      { icon: Globe, label: "Join Links" },
      { icon: Mail, label: "Community Support" },
      { icon: Crown, label: "Tournament Management" },
      { icon: Globe, label: "Merchandise Store" },
      { icon: Download, label: "CSV Export" },
      { icon: BarChart, label: "Advanced Reports" },
      { icon: Shield, label: "Payment Tracking" },
      { icon: Star, label: "Priority Support" },
    ],
  },
  premier: {
    id: "premier",
    name: "Cricso Premier",
    badge: "Best Value",
    badgeColor: "warning",
    description: "Built for academies, tournaments & businesses",
    maxClubs: 10,
    priceMonthly: 500,
    priceYearly: 4800,
    yearlySavings: 1200,
    features: [
      { icon: Users, label: "Up to 10 Clubs" },
      { icon: Users, label: "Unlimited Members" },
      { icon: Shield, label: "Match Polls" },
      { icon: Zap, label: "Team Generation" },
      { icon: Shield, label: "Expense Tracking" },
      { icon: Shield, label: "Match Fee Split" },
      { icon: BarChart, label: "Basic Ledger" },
      { icon: Globe, label: "Join Links" },
      { icon: Mail, label: "Community Support" },
      { icon: Crown, label: "Tournament Management" },
      { icon: Globe, label: "Merchandise Store" },
      { icon: Download, label: "CSV Export" },
      { icon: BarChart, label: "Advanced Reports" },
      { icon: Shield, label: "Payment Tracking" },
      { icon: Star, label: "Priority Support" },
      { icon: Brain, label: "AI Team Balancing" },
      { icon: Brain, label: "AI Match Insights" },
      { icon: Palette, label: "Custom Branding" },
      { icon: BarChart, label: "Advanced Analytics" },
      { icon: Users, label: "Multi-Admin Management" },
      { icon: Code, label: "API Access (future)" },
      { icon: Star, label: "Early Access Features" },
      { icon: Star, label: "Premium Support" },
    ],
  },
};

const PLAN_ORDER = ["starter", "league", "premier"];

export default function SubscriptionPage() {
  const nav = useNavigate();
  const { user, refreshMe } = useAuth();
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [currentPlan, setCurrentPlan] = useState("starter");
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [showInvoices, setShowInvoices] = useState(true);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [{ data: sub }, { data: invs }, { data: pays }] = await Promise.all([
        api.get("/subscription/current"),
        api.get("/subscription/invoices"),
        api.get("/subscription/payments"),
      ]);
      setSubscription(sub);
      setCurrentPlan(sub.plan_id || "starter");
      setInvoices(invs);
      setPayments(pays);
    } catch (err) {
      console.error("Failed to fetch subscription data:", err);
      setCurrentPlan("starter");
    } finally {
      setLoading(false);
    }
  };

  const getPlan = (id) => PLANS[id];

  const isCurrentPlan = (planId) => currentPlan === planId;
  const isUpgrade = (planId) => PLAN_ORDER.indexOf(planId) > PLAN_ORDER.indexOf(currentPlan);
  const isDowngrade = (planId) => PLAN_ORDER.indexOf(planId) < PLAN_ORDER.indexOf(currentPlan);

  const handleChangePlan = async (planId) => {
    if (!user) {
      nav("/login", { state: { from: "/subscription" } });
      return;
    }
    if (isCurrentPlan(planId)) return;

    setActionLoading(planId);
    try {
      await api.post("/subscription/change", {
        plan_id: planId,
        billing_cycle: billingCycle,
      });
      toast.success(isUpgrade(planId) ? "Upgraded successfully!" : "Plan will change at period end");
      await refreshMe();
      await fetchData();
      setShowUpgradeModal(null);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      nav("/login", { state: { from: "/subscription" } });
      return;
    }
    setActionLoading(planId);
    try {
      const plan = getPlan(planId);
      const { data } = await api.post("/subscription/create", {
        plan_id: planId,
        billing_cycle: billingCycle,
      });

      if (data.requires_payment) {
        await loadRazorpay();
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Cricso",
          description: `${plan.name} - ${billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)}`,
          order_id: data.order_id,
          handler: async (response) => {
            await api.post("/subscription/verify-payment", {
              payment_id: data.payment_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Subscription activated!");
            await refreshMe();
            await fetchData();
            nav("/home", { replace: true });
          },
          prefill: {
            email: user.email,
            name: user.name,
          },
          theme: {
            color: "#059669",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", async (response) => {
          toast.error("Payment failed: " + response.error.description);
          setActionLoading(null);
        });
        rzp.open();
      } else {
        toast.success("Subscription activated!");
        await refreshMe();
        await fetchData();
        nav("/home", { replace: true });
      }
    } catch (err) {
      toast.error(apiError(err));
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel subscription? Your plan will remain active until the end of the billing period.")) return;
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription will cancel at period end");
      await fetchData();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleReactivate = async () => {
    try {
      await api.post("/subscription/reactivate");
      toast.success("Subscription reactivated");
      await fetchData();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const price = (plan) => billingCycle === "yearly" ? plan.priceYearly : plan.priceMonthly;
  const pricePerMonth = (plan) => billingCycle === "yearly" ? Math.round(plan.priceYearly / 12) : plan.priceMonthly;

  if (loading) {
    return (
      <div className="app-shell flex flex-col min-h-screen bg-white">
        <div className="flex-1 grid place-items-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </div>
    );
  }

  const plan = getPlan(currentPlan);
  const isCancelled = subscription?.cancel_at_period_end;
  const periodEnd = subscription?.current_period_end;

  return (
    <div className="app-shell flex flex-col min-h-screen bg-white">
      <div className="px-5 pt-8 pb-4 max-w-5xl mx-auto w-full">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Subscription</h1>
            <p className="text-ink-muted mt-1">Manage your Cricso plan and billing</p>
          </div>
          <Button variant="outline" onClick={() => nav("/pricing")} data-testid="view-all-plans-btn">
            View All Plans <ArrowUpRight size={16} />
          </Button>
        </div>

        {/* Current Plan Card */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center">
                {currentPlan === "starter" && <Crown size={28} className="text-brand" />}
                {currentPlan === "league" && <Star size={28} className="text-blue-600" />}
                {currentPlan === "premier" && <Star size={28} className="text-amber-600" />}
              </div>
              <div>
                <h2 className="text-xl font-bold">{plan.name}</h2>
                <p className="text-ink-muted text-sm">{plan.description}</p>
                <div className="flex items-center gap-3 mt-2">
                  <Chip tone={plan.badgeColor} className="text-xs">{plan.badge}</Chip>
                  <span className="text-xs text-ink-muted">{plan.maxClubs} clubs max</span>
                  {isCancelled && (
                    <Chip tone="danger" className="text-xs">
                      <X size={10} /> Cancelling at period end
                    </Chip>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-right">
                <div className="text-2xl font-bold text-ink">{price(plan) === 0 ? "FREE" : inr(price(plan))}</div>
                <div className="text-sm text-ink-muted">/{billingCycle === "yearly" ? "yr" : "mo"}</div>
                {price(plan) > 0 && billingCycle === "yearly" && (
                  <div className="text-xs text-brand">Save {inr(plan.yearlySavings)}/yr</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBillingCycle("monthly")}
                  className={billingCycle === "monthly" ? "bg-white shadow-sm" : ""}
                >
                  Monthly
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBillingCycle("yearly")}
                  className={billingCycle === "yearly" ? "bg-white shadow-sm" : ""}
                >
                  Yearly <span className="text-xs text-green-600 font-medium">Save</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Period info */}
          {periodEnd && (
            <div className="mt-4 pt-4 border-t border-black/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-ink-muted">
                <Calendar size={14} />
                <span>
                  {isCancelled ? "Cancels on " : "Renews on "} {fmtDate(periodEnd)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isCancelled ? (
                  <Button variant="primary" size="sm" onClick={handleReactivate} data-testid="reactivate-btn">
                    <RotateCcw size={14} /> Reactivate
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleCancel} data-testid="cancel-btn">
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Upgrade/Downgrade Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Available Plans</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {PLAN_ORDER.map((planId) => (
              <PlanCard
                key={planId}
                plan={getPlan(planId)}
                isCurrent={isCurrentPlan(planId)}
                isUpgrade={isUpgrade(planId)}
                isDowngrade={isDowngrade(planId)}
                price={price(getPlan(planId))}
                pricePerMonth={pricePerMonth(getPlan(planId))}
                billingCycle={billingCycle}
                loading={actionLoading === planId}
                onSelect={isCurrentPlan(planId) ? null : handleChangePlan}
                onSubscribe={isCurrentPlan(planId) ? null : isUpgrade(planId) ? handleSubscribe : null}
              />
            ))}
          </div>
        </div>

        {/* Billing History */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Billing History</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowInvoices(!showInvoices)}>
              {showInvoices ? "Hide" : "Show"} Invoices
            </Button>
          </div>

          {showInvoices && invoices.length > 0 && (
            <Card className="overflow-hidden">
              <div className="divide-y divide-black/5">
                {invoices.map((inv) => (
                  <InvoiceRow key={inv.id} invoice={inv} />
                ))}
              </div>
            </Card>
          )}

          {!showInvoices && invoices.length > 0 && (
            <p className="text-center text-ink-muted py-4">{invoices.length} invoice{invoices.length > 1 ? "s" : ""} available</p>
          )}

          {invoices.length === 0 && (
            <Card className="text-center py-8">
              <FileText size={32} className="mx-auto text-ink-muted/50 mb-3" />
              <p className="text-ink-muted">No invoices yet</p>
            </Card>
          )}
        </div>

        {/* Payment History */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Payment History</h3>
          {payments.length > 0 ? (
            <Card className="overflow-hidden">
              <div className="divide-y divide-black/5">
                {payments.map((pay) => (
                  <PaymentRow key={pay.id} payment={pay} />
                ))}
              </div>
            </Card>
          ) : (
            <Card className="text-center py-8">
              <CreditCard size={32} className="mx-auto text-ink-muted/50 mb-3" />
              <p className="text-ink-muted">No payments yet</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, isCurrent, isUpgrade, isDowngrade, price, pricePerMonth, billingCycle, loading, onSelect, onSubscribe }) {
  const badgeColors = {
    success: "bg-brand-100 text-brand-700",
    default: "bg-blue-100 text-blue-700",
    warning: "bg-amber-100 text-amber-700",
  };

  const handleClick = () => {
    if (onSubscribe) onSubscribe(plan.id);
    else if (onSelect) onSelect(plan.id);
  };

  return (
    <Card className={`relative flex flex-col h-full ${isCurrent ? "ring-2 ring-brand" : ""}`}>
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${badgeColors[plan.badgeColor]}`}>
            {plan.badge}
          </span>
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
        <p className="text-ink-muted text-sm">{plan.description}</p>
      </div>

      <div className="mb-6">
        {price === 0 ? (
          <div className="text-4xl font-bold text-ink">FREE</div>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-ink">{inr(price)}</span>
            <span className="text-ink-muted">/{billingCycle === "yearly" ? "yr" : "mo"}</span>
          </div>
        )}
        {price > 0 && billingCycle === "yearly" && (
          <p className="text-sm text-brand mt-1">
            {inr(pricePerMonth)}/mo • Save {inr(plan.yearlySavings)}/yr
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 mb-6">
        <span className={`chip ${isCurrent ? "bg-brand text-white" : "bg-black/5 text-ink"}`}>
          {plan.maxClubs} Clubs Max
        </span>
      </div>

      <div className="flex-1 space-y-3 mb-6">
        {plan.features.map((feature, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            <feature.icon size={16} className="text-brand" />
            <span className="text-ink">{feature.label}</span>
            <Check size={14} className="text-brand" />
          </div>
        ))}
      </div>

      <Button
        className="w-full"
        size="lg"
        variant={isCurrent ? "outline" : isUpgrade ? "primary" : "dark"}
        disabled={loading || isCurrent}
        onClick={handleClick}
        data-testid={`plan-${plan.id}-btn`}
      >
        {loading ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Processing…
          </>
        ) : isCurrent ? (
          "Current Plan"
        ) : isUpgrade ? (
          <>
            <ArrowRight size={18} /> Upgrade Now
          </>
        ) : (
          <>
            <ArrowRight size={18} /> Switch Plan
          </>
        )}
      </Button>

      {isDowngrade && (
        <p className="text-center text-xs text-ink-muted mt-3">
          Takes effect at period end
        </p>
      )}
    </Card>
  );
}

function InvoiceRow({ invoice }) {
  const statusColors = {
    paid: "success",
    pending: "warning",
    failed: "danger",
    refunded: "default",
  };

  return (
    <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center gap-3">
        <FileText size={20} className="text-ink-muted" />
        <div>
          <div className="font-semibold text-ink">{invoice.invoice_number}</div>
          <div className="text-sm text-ink-muted">
            {fmtDate(invoice.period_start)} – {fmtDate(invoice.period_end)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className="font-semibold text-ink">{inr(invoice.amount)}</span>
        <Chip tone={statusColors[invoice.status] || "default"} className="text-xs capitalize">
          {invoice.status}
        </Chip>
        {invoice.status === "paid" && (
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => window.open(invoice.pdf_url, "_blank")}>
            <FileText size={14} /> Download
          </Button>
        )}
      </div>
    </div>
  );
}

function PaymentRow({ payment }) {
  const statusColors = {
    captured: "success",
    created: "warning",
    failed: "danger",
    refunded: "default",
  };

  const statusLabels = {
    captured: "Paid",
    created: "Pending",
    failed: "Failed",
    refunded: "Refunded",
  };

  return (
    <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center gap-3">
        <CreditCard size={20} className="text-ink-muted" />
        <div>
          <div className="font-semibold text-ink">{payment.description || "Payment"}</div>
          <div className="text-sm text-ink-muted">{fmtDate(payment.created_at)}</div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className="font-semibold text-ink">{inr(payment.amount)}</span>
        <Chip tone={statusColors[payment.status] || "default"} className="text-xs capitalize">
          {statusLabels[payment.status] || payment.status}
        </Chip>
      </div>
    </div>
  );
}